import { Close, Edit, InfoOutlined } from "@mui/icons-material";
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { EstimateModal, InsuranceDetailsModal } from "../EditReport";
import EditKeyAndAdditional from "./EditKeyandAdditional";
import { DentsModal } from "./DentsModal";
import { PortalContext } from "../../../../context/carPortalContext";

// ---------------------------------------------------------------------
// Helper functions
// ---------------------------------------------------------------------

// ---------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------
const KeyInformation = ({
  reportData,
  insuranceData: initialInsuranceData,
  dentsData: initialDentsData,
  tyreLifeData: initialTyreLifeData,
  majorRepairData: initialMajorRepairData,
  minorRepairData: initialMinorRepairData,
  isPublished,
  transitionData,
}) => {
  const {
    keyInfo,
    setKeyInfo,
    updateKeyInfo,
    insuranceData,
    updateInsuranceData,
    dentsData,
    setDentsData,
    tyreLifeData,
    updateTyreLifeData,
    majorRepairs,
    setMajorRepairs,
    minorRepairs,
    setMinorRepairs,
    removeRepair,
    hasDentDetails,
    averageTyreLife,
    hasTyreLifeData,
  } = useContext(PortalContext);
  const [isEditing, setIsEditing] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [selectedEstimate, setSelectedEstimate] = useState([]);
  const [modalTitle, setModalTitle] = useState("");
  const [isTyreLifeModalOpen, setIsTyreLifeModalOpen] = useState(false);
  const [openInsuranceModal, setOpenInsuranceModal] = useState(false);
  const [isDentsModalOpen, setIsDentsModalOpen] = useState(false);
  const [infoDialogOpen, setInfoDialogOpen] = useState(false);
  // Initialize our context states from initial props
  const initialKeyInfo = reportData?.keyInformation;
  useEffect(() => {
    if (initialKeyInfo) {
      setKeyInfo((prev) => ({
        ...prev,
        ...initialKeyInfo,
        majorAccident: initialKeyInfo.majorAccident || reportData?.keyInformation?.majorAccident || "NO",
        floodDamage: initialKeyInfo.floodDamage || reportData?.keyInformation?.floodDamage || "NO",
        engineCondition: initialKeyInfo.engineCondition || reportData?.keyInformation?.engineCondition || "AVERAGE",
        transmission: initialKeyInfo.transmission || reportData?.keyInformation?.transmission || "AVERAGE",
        exteriorCondition: initialKeyInfo.exteriorCondition || reportData?.keyInformation?.exteriorCondition || "AVERAGE",
        interiorCondition: initialKeyInfo.interiorCondition || reportData?.keyInformation?.interiorCondition || "AVERAGE",
        kmsDriven: initialKeyInfo.kmsDriven || ""
      }));
    }
    if (initialInsuranceData) {
      updateInsuranceData(initialInsuranceData);
    }
    if (initialDentsData) {
      setDentsData((prev) => ({ ...prev, ...initialDentsData }));
    }
    if (initialTyreLifeData) {
      updateTyreLifeData((prev) => ({ ...prev, ...initialTyreLifeData }));
    }
    if (initialMajorRepairData) {
      console.log("Initial Major Repair Data:", initialMajorRepairData);
      setMajorRepairs([...initialMajorRepairData]);
    }
    if (initialMinorRepairData) {
      console.log("Initial Minor Repair Data:", initialMinorRepairData);
      setMinorRepairs([...initialMinorRepairData]);
    }
  }, [
    initialInsuranceData,
    initialDentsData,
    initialTyreLifeData,
    initialMajorRepairData,
    initialMinorRepairData,
    reportData
  ]);

  // Add a new useEffect to handle keyInfo updates
  useEffect(() => {
    console.log("KeyInfo updated:", keyInfo);
  }, [keyInfo]);

  // -------------------------------------------------------------------
  // Compute average tyre life based on context tyreLifeData (if available)
  // -------------------------------------------------------------------
  const averageTyreLife1 = useMemo(() => {
    const tyreKeys = Object.keys(tyreLifeData);
    const validLifeValues = tyreKeys.filter(
      (key) => key !== "tyreLife" && tyreLifeData[key].endsWith("%")
    );
    const totalLife = validLifeValues.reduce((acc, key) => {
      const value = parseInt(tyreLifeData[key].replace("%", ""), 10);
      return acc + (isNaN(value) ? 0 : value);
    }, 0);
    return validLifeValues.length > 0
      ? (totalLife / validLifeValues.length).toFixed(0) + "%"
      : "-";
  }, [tyreLifeData]);

  // -------------------------------------------------------------------
  // Insurance expiry & status logic (same as before)
  // -------------------------------------------------------------------
  const calculateInsuranceExpiry = (expiryDate) => {
    const today = new Date();
    const expirationDate = new Date(expiryDate);
    const diffTime = expirationDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  
    return diffDays < 0 ? (
      <span style={{ color: "red" }}>
        Expired {Math.abs(diffDays)} days ago
      </span>
    ) : (
      <span style={{ color: "green" }}>
        Expires in {diffDays} days
      </span>
    );
  };

  const insuranceStatus = useMemo(() => {
    const today = new Date();
    const expiry = new Date(insuranceData?.expiryDate);
    return expiry >= today ? "Valid" : "Expired";
  }, [insuranceData?.expiryDate]);

  const insuranceDetails = {
    insurer: insuranceData?.insurer || "Not Available",
    number: insuranceData?.policyNumber || "Not Available",
    expiryDate: insuranceData?.expiryDate || "Not Available",
    expiresIn: insuranceData?.expiryDate
      ? calculateInsuranceExpiry(insuranceData?.expiryDate)
      : "Not Available",
    status: insuranceData?.expiryDate
      ? new Date(insuranceData?.expiryDate) >= new Date()
        ? "Valid"
        : "Expired"
      : "Not Available",
  };

  const marketValue = reportData?.aiInsights?.marketValue || reportData?.marketValue || "";
const selectedCurrency = marketValue.toString().includes("RM") ? "MYR" : "INR";
const locale = selectedCurrency === "MYR" ? "en-MY" : "en-IN";

  // -------------------------------------------------------------------
  // Repairs totals
  // -------------------------------------------------------------------
  const calculateTotal = (repairs) => {
    if (!Array.isArray(repairs)) {
      console.error("Invalid repairs data:", repairs);
      return 0;
    }
    return repairs.reduce((acc, repair) => {
      const costString = repair?.price || "";
      const numericValue = parseFloat(costString.replace(/[^\d.-]/g, ""));
      return acc + (isNaN(numericValue) ? 0 : numericValue);
    }, 0);
  };

  const totalMajorRepairs = useMemo(() => {
    const total = calculateTotal(majorRepairs);
    return new Intl.NumberFormat(locale, {
      style: "currency",
      currency: selectedCurrency,
      maximumFractionDigits: 0,
    }).format(total);
  }, [majorRepairs]);

  const totalMinorRepairs = useMemo(() => {
    const total = calculateTotal(minorRepairs);
    return new Intl.NumberFormat(locale, {
      style: "currency",
      currency: selectedCurrency,
      maximumFractionDigits: 0,
    }).format(total);
  }, [minorRepairs]);

  // -------------------------------------------------------------------
  // Dents and Tyre Life booleans (for UI display)
  // -------------------------------------------------------------------
  const hasDents = keyInfo?.dentsOnBody?.dents === "YES";
  const hasDentDetails1 = Object.entries(dentsData).some(
    ([_, details]) => details.Dents || details.Scratches
  );
  const hasTyreLifeData1 =
    tyreLifeData &&
    tyreLifeData["Front-Left"] &&
    tyreLifeData["Front-Right"] &&
    tyreLifeData["Rear-Left"] &&
    tyreLifeData["Rear-Right"];

  // -------------------------------------------------------------------
  // Parse categoryData (from context) and compute computed values
  // -------------------------------------------------------------------



  // EXTERIOR INSPECTION: Compute exterior condition and extract dents/scratches details
 
console.log("Key Information Data:", keyInfo);
  const handleInfoDialogOpen = () => {
    if (keyInfo?.majorAccident.toLowerCase() === "no") {
      setInfoDialogOpen(true);
    }
  };
  const handleInfoDialogClose = () => setInfoDialogOpen(false);

  const handleOpenTyreLifeModal = () => setIsTyreLifeModalOpen(true);
  const handleCloseTyreLifeModal = () => setIsTyreLifeModalOpen(false);
  const handleOpenDentsModal = () => setIsDentsModalOpen(true);
  const handleCloseDentsModal = () => setIsDentsModalOpen(false);
  const handleOpenInsuranceModal = () => setOpenInsuranceModal(true);
  const handleCloseInsuranceModal = () => setOpenInsuranceModal(false);
  const handleOpenModal = (repairType) => {
    setSelectedEstimate(
      repairType === "Major Repairs" ? majorRepairs : minorRepairs
    );
    setModalTitle(`${repairType} Estimate`);
    setOpenModal(true);
  };
  const handleCloseModal = () => setOpenModal(false);
  const handleOpenEdit = () => setIsEditing(true);
  const handleCloseEdit = () => setIsEditing(false);

  useEffect(() => {
    console.log("Key Info updated in KeyInformation:", keyInfo);
  }, [keyInfo]);

  return (
    <div className="key-information-container">
      <h3 className="key-information__title">{transitionData?.keyInformation}</h3>
      <div className="key-information__grid">
        {/* Kms Driven */}
        <div className="vehicle-info-item">
          <span className="vehicle-info-label">{transitionData?.kmsDriven}</span>
          <span className="vehicle-info-value highlighted">
            {keyInfo?.kmsDriven ? keyInfo?.kmsDriven : initialKeyInfo?.kmsDriven ?? '-'}
          </span>
        </div>
        {/* Major Accident */}
        <div className="key-info-item">
          <span className="key-info-label">{transitionData?.majorAccident}</span>
          <span className="key-info-value" style={{ textDecoration: keyInfo?.majorAccident === "NO" ? "underline" : "", display: 'flex', alignItems: 'center' }}>{keyInfo?.majorAccident} {keyInfo?.majorAccident === "NO" && (
              <Tooltip
                title="Click for more information"
                onClick={handleInfoDialogOpen}
              >
                <span
                  style={{
                    color: "red",
                    cursor: "pointer",
                    marginLeft: "4px",
                    paddingTop: "4px",
                    display: "flex",
                  }}
                >
                  <InfoOutlined style={{ fontSize: "20px" }} />
                </span>
              </Tooltip>
            )}
          </span>
        </div>
        {/* Flood Damage */}
        <div className="key-info-item">
          <span className="key-info-label">{transitionData?.floodDamage}</span>
          <span className="key-info-value">
            {keyInfo?.floodDamage ? keyInfo?.floodDamage : "-"}
          </span>
        </div>
        {/* Engine Condition */}
        <div className="key-info-item">
          <span className="key-info-label">{transitionData?.engineCondition}</span>
          <span className="key-info-value highlighted-yellow">
            {keyInfo?.engineCondition ? keyInfo?.engineCondition : "-"}
          </span>
        </div>
        {/* Transmission */}
        <div className="key-info-item">
          <span className="key-info-label">{transitionData?.transmission}</span>
          <span className="key-info-value highlighted-yellow">
            {keyInfo?.transmission ? keyInfo?.transmission : "-"}
          </span>
        </div>
        {/* Exterior Condition */}
        <div className="key-info-item">
          <span className="key-info-label">{transitionData?.exteriorCondition}</span>
          <span className="key-info-value highlighted-red">
            {keyInfo?.exteriorCondition ? keyInfo?.exteriorCondition : "-"}
          </span>
        </div>
        {/* Interior Condition */}
        <div className="key-info-item">
          <span className="key-info-label">{transitionData?.interiorCondition}</span>
          <span className="key-info-value highlighted-red">
            {keyInfo?.interiorCondition ? keyInfo?.interiorCondition : "-"}
          </span>
        </div>
        {/* Dents & Scratches */}
        <div className="key-info-item">
          <span className="key-info-label">{transitionData?.dentsScratches}</span>
          <span className="key-info-value highlighted-red">
            {(hasDents || hasDentDetails) ? "YES" : "-"}{" "}
            {(hasDents || hasDentDetails) && (
              <span
                style={{
                  fontSize: "12px",
                  color: "#003cc5",
                  textDecoration: "underline",
                  cursor: "pointer",
                }}
                onClick={handleOpenDentsModal}
              >
                Details
              </span>
            )}
          </span>
        </div>
        {/* Avg Tyre Life */}
        <div className="key-info-item">
          <span className="key-info-label">{transitionData?.avgTyreLife}</span>
          <span
            className="key-info-value"
            style={{ display: "flex", gap: "5px", alignItems: "baseline" }}
          >
            {averageTyreLife || "-"}
            {hasTyreLifeData && (
              <span
                style={{
                  fontSize: "12px",
                  color: "#003cc5",
                  textDecoration: "underline",
                  cursor: "pointer",
                }}
                onClick={handleOpenTyreLifeModal}
              >
                Details
              </span>
            )}
          </span>
        </div>
        {/* Insurance */}
        <div className="key-info-item">
          <span className="key-info-label">{transitionData?.insurance}</span>
          <span
            className="key-info-value highlighted-red"
            style={{
              display: "flex",
              gap: "5px",
              alignItems: "baseline",
              color:
                insuranceDetails?.status === "Valid" ? "#388e3c" : "#d32f2f",
            }}
          >
            {insuranceDetails.status}
            <span
              style={{
                fontSize: "12px",
                color: "#003cc5",
                textDecoration: "underline",
                cursor: "pointer",
              }}
              onClick={handleOpenInsuranceModal}
            >
              Details
            </span>
          </span>
        </div>
        {/* Major Repair */}
        <div className="key-info-item">
          <span className="key-info-label">{transitionData?.majorRepair}</span>
          <span
            className="key-info-value"
            style={{
              display: "flex",
              gap: "5px",
              alignItems: "baseline",
              color: "#000",
              fontWeight: "600",
            }}
          >
            {totalMajorRepairs}
            <span
              style={{
                fontSize: "12px",
                color: "#003cc5",
                textDecoration: "underline",
                cursor: "pointer",
              }}
              onClick={() => handleOpenModal("Major Repairs")}
            >
              Estimate
            </span>
          </span>
        </div>
        {/* Minor Repair */}
        <div className="key-info-item">
          <span className="key-info-label">{transitionData?.minorRepair}</span>
          <span
            className="key-info-value"
            style={{
              display: "flex",
              gap: "5px",
              alignItems: "baseline",
              fontWeight: "600",
            }}
          >
            {totalMinorRepairs}
            <span
              style={{
                fontSize: "12px",
                color: "#003cc5",
                textDecoration: "underline",
                cursor: "pointer",
              }}
              onClick={() => handleOpenModal("Minor Repairs")}
            >
              Estimate
            </span>
          </span>
        </div>
      </div>
      {!isPublished && (
        <IconButton
          onClick={handleOpenEdit}
          style={{ position: "absolute", top: 16, right: 16 }}
        >
          <Edit />
        </IconButton>
      )}
      <Dialog
        open={isEditing}
        onClose={handleCloseEdit}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: "12px",
          },
        }}
      >
        <DialogTitle>Edit Key and Additional Information</DialogTitle>
        <DialogContent>
          <EditKeyAndAdditional
            onClose={handleCloseEdit}
            keyInfo={keyInfo}
            updateParentState={updateKeyInfo}
          />
        </DialogContent>
      </Dialog>
      <EstimateModal
        open={openModal}
        handleClose={handleCloseModal}
        estimates={selectedEstimate}
        title={modalTitle}
        handleDelete={removeRepair}
        isPublished={isPublished}
      />
      <InsuranceDetailsModal
        open={openInsuranceModal}
        onClose={handleCloseInsuranceModal}
        insuranceDetails={insuranceDetails}
      />
      <DentsModal
        open={isDentsModalOpen}
        onClose={handleCloseDentsModal}
        // images={images}
        dentsData={dentsData ?? {}}
      />
      <Dialog
        open={isTyreLifeModalOpen}
        onClose={handleCloseTyreLifeModal}
        fullWidth
        maxWidth="xs"
        PaperProps={{
          sx: { borderRadius: "16px" },
        }}
      >
        <DialogTitle sx={{ color: "#003cc5" }}>
          Tyre Life Details
          <IconButton
            aria-label="close"
            onClick={handleCloseTyreLifeModal}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ paddingBottom: "30px" }}>
          <Grid container spacing={2}>
            {tyreLifeData ? (
              Object.entries(tyreLifeData).map(([tyre, lifePercentage]) => {
                if (tyre.toLowerCase() !== "average") {
                  return (
                    <Grid item xs={6} key={tyre}>
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 500,
                          color: "#4b5563",
                          fontSize: "0.9rem",
                          fontFamily: "Manrope, sans-serif",
                        }}
                      >
                        {tyre.replace(/-/g, " ")}:
                      </Typography>
                      <Box display="flex" alignItems="end">
                        <Typography
                          variant="body1"
                          sx={{
                            paddingTop: "4px !important",
                            color: "#000",
                            fontSize: "15px",
                            fontWeight: 700,
                            fontFamily: "Manrope, sans-serif",
                          }}
                        >
                          {lifePercentage}
                        </Typography>
                      </Box>
                    </Grid>
                  );
                }
                return null;
              })
            ) : (
              <Grid item xs={12}>
                <Typography>No tyre life data available.</Typography>
              </Grid>
            )}
          </Grid>
        </DialogContent>
      </Dialog>
      <Dialog
        open={infoDialogOpen}
        onClose={handleInfoDialogClose}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Major Accident Information</DialogTitle>
        <DialogContent>
          Based on physical inspection, the mechanic didn't find any evidence
          of an accident. For further information, please contact the authorized
          service center or insurance provider.
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default KeyInformation;
